import { Close, ETextStyleVariant, Text } from '@outdoorsyco/bonfire';
import debounce from 'lodash/debounce';
import React, { useRef } from 'react';
import { useIntl } from 'react-intl';

import { genRetrieveMapboxUrl } from '@/hooks/useMapboxQuery';
import { MAX_RECENT_SEARCHES } from '@/hooks/useRecentSearchesWithFilters';
import { ESearchWidgetFilter } from '@/services/analytics/search/types';
import apiRequest from '@/services/apiRequest';
import { IMapboxRetrievedSuggestion } from '@/services/types/mapbox/mapboxApi';
import { mapLocationLabelToGroupName, setSurfacedLocation } from '@/utility/surfacedLocation';

import { FilterContent, TFilterContentSharedProps } from './FilterContent';
import { EHomeSearchWidgetVariant } from './HomeSearchWidget';
import { useLocationFilterContext } from './LocationFilterContext';

type TLocationFilterContentProps = TFilterContentSharedProps & {
  variant: EHomeSearchWidgetVariant;
  onDataSelection?: () => void;
  onTrackFilterToggled?: (
    oldSelection: Record<string, unknown>,
    newSelection: Record<string, unknown>,
    modifiedWidgetFilter: ESearchWidgetFilter,
  ) => void;
};

export const LocationFilterContent = ({
  variant,
  onDataSelection,
  onTrackFilterToggled,
  ...props
}: TLocationFilterContentProps) => {
  const intl = useIntl();

  const {
    addressValue,
    setAddressValue,
    suggestions,
    recentSearches,
    popularDestinations,
    navigateToRecentSearch,
    setMapboxValues,
  } = useLocationFilterContext();

  const debouncedTrackFilterToggledRef = useRef(
    debounce((oldValue: string, newValue: string) => {
      onTrackFilterToggled?.(
        {
          location: oldValue,
        },
        {
          location: newValue,
        },
        ESearchWidgetFilter.Location,
      );
    }, 500),
  );

  // Display search results if they searched.
  // Otherwise fallback to recent searches, and
  // if there are none, display the popular searches.
  const { options, optionsTitle } = (() => {
    if (suggestions.length) {
      return {
        options: suggestions,
        optionsTitle: intl.formatMessage({
          defaultMessage: 'Matches',
          id: 'VLIHLz',
          description: 'Global Header > Search',
        }),
      };
    }

    // For auto rentals search show only suggestions.
    if (variant === EHomeSearchWidgetVariant.AutoRentals) {
      return {};
    }

    if (recentSearches.length) {
      return {
        options: recentSearches.slice(0, MAX_RECENT_SEARCHES),
        optionsTitle: intl.formatMessage({
          defaultMessage: 'Recent Searches',
          id: 'yLl55w',
          description: 'Global Header > Search',
        }),
      };
    }

    return {
      options: popularDestinations,
      optionsTitle: intl.formatMessage({
        defaultMessage: 'Popular Destinations',
        id: 'zWHSAh',
        description: 'Global Header > Search',
      }),
    };
  })();

  const handleAddressChange = (value: string) => {
    setAddressValue(value);
    // on address change, debounce the tracking to not spam analytics
    debouncedTrackFilterToggledRef.current?.(addressValue, value);
  };

  const handleSelectOption = async (option: any) => {
    if (option?.url) {
      navigateToRecentSearch(option.url);
      return;
    }

    setAddressValue(option.label);

    if (option?.value?.mapbox_id) {
      let fullAddress = '';
      if (option?.value?.full_address) {
        // when suggest returns a full address use that and avoid retrieve call
        fullAddress = option.value.full_address;
      } else {
        const url = genRetrieveMapboxUrl(option.value.mapbox_id);
        if (url) {
          const retrievedSuggestion = await apiRequest<IMapboxRetrievedSuggestion>({
            url,
          });
          fullAddress = retrievedSuggestion.features[0]?.properties.full_address || '';
        }
      }

      setMapboxValues(option.value.mapbox_id, fullAddress);
    }

    setSurfacedLocation(mapLocationLabelToGroupName(optionsTitle));
    onDataSelection?.();

    if (option?.label) {
      onTrackFilterToggled?.(
        {
          location: addressValue,
        },
        {
          location: option.label,
        },
        ESearchWidgetFilter.Location,
      );
    }
  };

  return (
    <FilterContent
      header={
        <>
          {intl.formatMessage({
            defaultMessage: 'Where to?',
            id: 'XaSWrh',
            description: 'Home Search Widget > Location Modal Title',
          })}
        </>
      }
      content={
        <div className="flex flex-col h-full">
          <div className="flex flex-col gap-2">
            <Text
              variant={ETextStyleVariant.Label}
              component="span"
              className="w-full truncate text-gray-50">
              {intl.formatMessage({
                defaultMessage: 'Location',
                id: 'HJy2Bx',
                description: 'Home Search Widget > Location Filter Label',
              })}
            </Text>
            <div className="relative">
              <input
                placeholder={intl.formatMessage({
                  defaultMessage: 'Type to search',
                  id: 'xWNpY0',
                  description: 'Home Search Widget > Location Filter Input Placeholder',
                })}
                value={addressValue}
                onChange={e => handleAddressChange(e.target.value)}
                autoCapitalize="none"
                autoComplete="off"
                spellCheck="false"
                className="w-full pb-2 font-medium tracking-normal text-gray-800 bg-transparent border-b-2 rounded-none highlight placeholder-gray-310 border-gray-50"
              />
              {addressValue !== '' && (
                <Close onClose={() => setAddressValue('')} className="absolute top-0 right-0" />
              )}
            </div>
          </div>

          {optionsTitle && (
            <Text variant={ETextStyleVariant.Label} className="pt-6 pb-2 text-gray-500">
              {optionsTitle}
            </Text>
          )}

          {options && (
            <div className="flex-grow overflow-auto">
              <ul>
                {options.map(option => (
                  <Text
                    key={option.label}
                    variant={ETextStyleVariant.MediumRegular}
                    component="li"
                    role="option"
                    onClick={() => {
                      handleSelectOption(option);
                    }}
                    className="cursor-pointer py-1.5">
                    {option.label}
                    {!!option.sublabel && <div className="text-gray-50">{option.sublabel}</div>}
                  </Text>
                ))}
              </ul>
            </div>
          )}
        </div>
      }
      {...props}
    />
  );
};
